import { useCallback, useEffect, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import API from '@enums/api'
import { PodiumFleetPagination, PodiumJobRow, SessionTitle } from '@types'
import useStoredPreference from '@hooks/useStoredPreference'
import CardPagination from './CardPagination'
import CopyableId from './CopyableId'
import { isPerPageOption } from './pageSizes'

interface Props {
    hardwareId: string
    /**
     * A decommissioned device is wiped and unreachable — Habitat Admin refuses the retry
     * command with a 409 — so the history stays readable but the buttons go away.
     */
    retired?: boolean
    /** Called after a retry is accepted, so the page's header counts can catch up. */
    onRetried?: () => void
}

/** The server's coarse status axis, plus the two views support actually asks for. */
type StatusFilter = 'all' | 'failed' | 'active'

const DEFAULT_PER_PAGE = 25

/**
 * Both tabs are per-viewer conveniences, remembered across page loads and across
 * devices: support tends to live on one of these views for a whole shift.
 */
const FILTER_KEY = 'podium.jobsFilter'
const PER_PAGE_KEY = 'podium.jobsPerPage'

const isStatusFilter = (value: unknown): value is StatusFilter =>
    value === 'all' || value === 'failed' || value === 'active'

/**
 * What each tab asks the server for. "Active" spans two statuses, which is why the
 * endpoint takes a list — narrowing a fetched page client-side would leave the totals and
 * page numbers describing the unfiltered set.
 */
const FILTER_STATUSES: Record<
    StatusFilter,
    PodiumJobRow['status'][] | undefined
> = {
    all: undefined,
    failed: ['failed'],
    active: ['queued', 'running'],
}

/**
 * Where a job is, in one phrase — or nothing, when the status pill beside it already says
 * everything. `stage` carries the phase for every live state (the running stage,
 * `uploading` during upload, or the stage it died in); without one there is nothing to add
 * but the status, which is why a complete or queued job contributes no phrase at all.
 */
function describeStage(job: PodiumJobRow): string | null {
    // A live job wears its stage as a pill beside the status, so repeating it here would
    // put the same word on the row twice.
    if (job.status !== 'failed' || !job.stage) return null
    // Worth naming the failure even though the pill exists: an out-of-retries job's pill
    // reads "needs attention", not "failed".
    // Sentence-cased to sit level with the labelled timestamps after it.
    return `Failed at ${job.stage}`
}

/**
 * The job's two moments, spelled out: when the recording was made, and when the job
 * reached its final state. The device stamps `completed_at` on reaching a terminal status
 * and clears it again on requeue, so it means "finished" for a complete job and "failed"
 * for a failed one — never "still going".
 */
function describeTiming(job: PodiumJobRow): string[] {
    const parts: string[] = []
    const recordedAt = job.device_started_at
        ? dayjs(job.device_started_at)
        : null

    if (recordedAt) {
        parts.push(`Recording started: ${recordedAt.format('D MMM HH:mm')}`)
    }

    const finishedLabel =
        job.status === 'complete'
            ? 'Processing completed'
            : job.status === 'failed'
              ? 'Processing failed'
              : null
    if (job.completed_at && finishedLabel) {
        parts.push(
            `${finishedLabel}: ${dayjs(job.completed_at).format('D MMM HH:mm')}`
        )
    }

    return parts
}

function statusColour(
    job: PodiumJobRow
): 'error' | 'warning' | 'success' | 'info' | 'default' {
    if (job.needs_attention) return 'warning'
    if (job.status === 'failed') return 'error'
    if (job.status === 'complete') return 'success'
    if (job.status === 'running') return 'info'
    return 'default'
}

/**
 * Per-device processing jobs, newest device activity first, with the per-job Retry that is
 * the whole point of the card: a needs-attention job (auto-retry budget spent) can only move
 * again when a human asks.
 */
export default function PodiumJobsCard({
    hardwareId,
    retired,
    onRetried,
}: Props) {
    const [jobs, setJobs] = useState<PodiumJobRow[]>([])
    const [pagination, setPagination] = useState<PodiumFleetPagination | null>(
        null
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [filter, setFilter] = useStoredPreference<StatusFilter>(
        FILTER_KEY,
        'all',
        isStatusFilter
    )
    const [page, setPage] = useState(1)
    // Page NUMBER is deliberately not remembered: yesterday's page 2 holds different
    // jobs today, so restoring it would restore the wrong rows.
    const [perPage, setPerPage] = useStoredPreference<number>(
        PER_PAGE_KEY,
        DEFAULT_PER_PAGE,
        isPerPageOption
    )
    const [retrying, setRetrying] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)

    // Session id -> its title, or null for an id Messenger Pigeon has no session for.
    // Resolved separately from the jobs themselves and merged by id, so a slow or failed
    // lookup can never hold up, or take down, the list it decorates. The null entries are
    // what stop a missing session being re-requested on every render.
    const [titles, setTitles] = useState<Record<string, SessionTitle | null>>(
        {}
    )

    // Monotonic request id, checked after the await: a slow page fetch trailing a filter
    // switch must not paint old-filter rows over the new ones.
    const latestRequest = useRef(0)

    const fetchPage = useCallback(async () => {
        const requestId = latestRequest.current + 1
        latestRequest.current = requestId
        setLoading(true)
        setError(null)

        const res = await API.ha.podium
            .getJobs(hardwareId, {
                page,
                perPage,
                status: FILTER_STATUSES[filter],
            })
            .catch(() => null)
        if (requestId !== latestRequest.current) return
        setLoading(false)

        if (!res?.ok || !res.value?.pagination) {
            setError("Couldn't load the processing jobs.")
            return
        }

        setJobs(res.value.data)
        setPagination(res.value.pagination)
    }, [hardwareId, page, perPage, filter])

    useEffect(() => {
        void fetchPage()
    }, [fetchPage])

    // Name the jobs on this page: one lookup for the whole page, only for ids not already
    // resolved. Titles live in Messenger Pigeon and stay editable there, so they're read
    // live rather than copied onto the job rows.
    useEffect(() => {
        const unresolved = [
            ...new Set(
                jobs
                    .map((job) => job.session_id)
                    .filter((id): id is string => !!id && !(id in titles))
            ),
        ]
        if (unresolved.length === 0) return

        let abandoned = false
        void API.overviews
            .sessionTitles(unresolved)
            .then((res) => {
                if (abandoned || !res.ok || !res.value) return
                setTitles((prev) => ({
                    // Every id asked for is marked resolved; the ones with no session
                    // stay null and render as they did before.
                    ...Object.fromEntries(unresolved.map((id) => [id, null])),
                    ...prev,
                    ...res.value,
                }))
            })
            .catch(() => {
                // Titles are decoration. A failed lookup leaves the ids on screen.
            })

        return () => {
            abandoned = true
        }
    }, [jobs, titles])

    const retry = async (job: PodiumJobRow) => {
        if (!job.upload_id) return
        setRetrying(job.job_id)
        setNotice(null)
        const res = await API.ha.podium
            .retryJobs(hardwareId, job.upload_id)
            .catch(() => null)
        setRetrying(null)

        if (!res?.ok) {
            // The device's own refusals (unrecoverable reason, unknown upload) come back
            // as error EVENTS rather than as a failed request, so they surface in the
            // event history below rather than here.
            setNotice(
                'Retry was not delivered — the device may be unreachable. Try again shortly.'
            )
            return
        }
        setNotice(
            'Retry sent. The device reports progress as it works through it.'
        )
        void fetchPage()
        onRetried?.()
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h6">
                    Processing jobs
                    {pagination && pagination.total > 0 && (
                        <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                        >
                            {pagination.total}
                        </Typography>
                    )}
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filter}
                    onChange={(_e, v: StatusFilter | null) => {
                        if (!v) return
                        // A filter changes what page 1 even means, so never keep the
                        // current page number across a switch.
                        setFilter(v)
                        setPage(1)
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="failed">Failed</ToggleButton>
                    <ToggleButton value="active">Active</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {notice && (
                <Alert
                    severity="info"
                    onClose={() => setNotice(null)}
                    sx={{ mb: 2 }}
                >
                    {notice}
                </Alert>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => void fetchPage()}
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {!error && !loading && jobs.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    {filter === 'all'
                        ? 'No processing jobs reported yet. Devices running an older app version do not report them.'
                        : 'No jobs match this filter.'}
                </Typography>
            )}

            <Stack divider={<Divider flexItem />} spacing={1.5}>
                {jobs.map((job) => {
                    // Retry is for failed jobs the device can still act on: a row it no
                    // longer reports has lost its recording file, so nothing can re-run.
                    const retryable =
                        !retired &&
                        job.status === 'failed' &&
                        job.device_present &&
                        Boolean(job.upload_id) &&
                        (Boolean(job.recoverable) || job.needs_attention)

                    // Undefined while the lookup is in flight, null once we know there is
                    // no session for it — neither renders anything.
                    const session = job.session_id
                        ? titles[job.session_id]
                        : null

                    const line = [
                        describeStage(job),
                        ...describeTiming(job),
                    ].filter(Boolean)

                    return (
                        // Two columns: the detail wraps freely, the Retry button keeps its
                        // own lane on the right so a job with several chips doesn't push
                        // the button onto a line of its own.
                        <Stack
                            key={job.job_id}
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                        >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    <Chip
                                        label={
                                            job.needs_attention
                                                ? 'needs attention'
                                                : job.status
                                        }
                                        color={statusColour(job)}
                                        size="small"
                                        variant="outlined"
                                    />
                                    {job.status === 'running' && job.stage && (
                                        // Where a live job has got to. Uncoloured on
                                        // purpose: it qualifies the status pill next to
                                        // it rather than competing with it.
                                        <Chip
                                            label={job.stage}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                    {session && (
                                        // The recording's own name, from Messenger
                                        // Pigeon — what the lecturer typed when they
                                        // started it, and the only text on the row that
                                        // says what this job actually is.
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {session.title}
                                            {session.deletedAt && (
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    (session deleted)
                                                </Typography>
                                            )}
                                        </Typography>
                                    )}
                                    {job.failure_reason && (
                                        <Chip
                                            label={
                                                job.recoverable
                                                    ? `${job.failure_reason} · recoverable`
                                                    : job.failure_reason
                                            }
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                    {!job.device_present && (
                                        <Tooltip title="The device no longer reports this job — its recording has been pruned or reclaimed.">
                                            <Chip
                                                label="no longer on device"
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Tooltip>
                                    )}
                                    {job.retry_count > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {job.needs_attention
                                                ? `${job.retry_count} auto retries used`
                                                : `retry ${job.retry_count}`}
                                        </Typography>
                                    )}
                                </Stack>

                                {/* Descending weight down the row: the recording's name,
                                    then why it broke, then when, then the ids. */}
                                {job.error_message && (
                                    // Device-authored text: keep its line breaks and break
                                    // long unbroken tokens so one message can't widen the card.
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mt: 0.5,
                                            whiteSpace: 'pre-wrap',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {job.error_message}
                                    </Typography>
                                )}

                                {line.length > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.25, display: 'block' }}
                                    >
                                        {line.join(' · ')}
                                    </Typography>
                                )}

                                {(job.upload_id || job.session_id) && (
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{ mt: 0.25 }}
                                    >
                                        {job.upload_id && (
                                            <CopyableId
                                                label="upload"
                                                value={job.upload_id}
                                            />
                                        )}
                                        {job.session_id && (
                                            <CopyableId
                                                label="session"
                                                value={job.session_id}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </Box>

                            {retryable && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ flexShrink: 0 }}
                                    disabled={retrying === job.job_id}
                                    onClick={() => void retry(job)}
                                >
                                    {retrying === job.job_id
                                        ? 'Sending…'
                                        : 'Retry'}
                                </Button>
                            )}
                        </Stack>
                    )
                })}
            </Stack>

            {loading && (
                <Stack direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <CircularProgress size={20} />
                </Stack>
            )}

            <CardPagination
                pagination={pagination}
                onChange={(nextPage, nextPerPage) => {
                    setPage(nextPage)
                    setPerPage(nextPerPage)
                }}
            />
        </Paper>
    )
}
